import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { newsQueryOptions, type NewsArticle } from "@/lib/news.queries";
import {
  researchProductsQueryOptions,
  sortByPublishDateDesc,
  type ResearchProduct,
} from "@/lib/store.queries";

/**
 * Deliberately `useQuery`, not `useSuspenseQuery`: the Aurora cluster pauses
 * when idle and its first query can take ~15s. A suspended homepage would show
 * a bare "Loading…" — and the prerenderer refuses to snapshot a page stuck on a
 * Suspense fallback, so the old snapshot would silently keep serving.
 * Every consumer renders fine with an empty array.
 */
export function useLatestInsights(limit: number): NewsArticle[] {
  const { data } = useQuery(newsQueryOptions());
  return (data ?? []).slice(0, limit);
}

/** Newest publish date first (same order as /research), then the limit. */
export function useResearchProducts(limit?: number): ResearchProduct[] {
  const { data } = useQuery(researchProductsQueryOptions());
  const rows = useMemo(() => sortByPublishDateDesc(data ?? []), [data]);
  return limit ? rows.slice(0, limit) : rows;
}

/** Article destination: on-site page when we hold the body, else the source. */
export function articleHref(a: NewsArticle): string {
  return a.has_body ? `/news/${a.slug}` : (a.external_url ?? `/news/${a.slug}`);
}

export function formatArticleDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
